import {
  Link as RRLink,
  useLocation,
  useNavigate as useRRNavigate,
  useSearchParams as useRRSearchParams,
} from "react-router";
import type {
  NavigationAdapter,
  NavImageProps,
  NavLinkProps,
} from "@audra/marketing-ui";

/**
 * React Router 7 adapter for @audra/marketing-ui (flow web/frontend SPA).
 *
 * Mounted at the SPA root (`src/App.tsx`) via
 * `<NavigationProvider adapter={rrAdapter}>`. Provides the package's
 * components (currently `<LoginScreen>`) with React Router navigation
 * primitives — no `next/*` imports anywhere in the package.
 *
 * Mappings:
 *   - `Link.href`            → `RRLink.to`
 *   - `Image`                → plain `<img>` (no next/image equivalent;
 *                              `priority` is Next-only so we drop it)
 *   - `usePathname`          → `useLocation().pathname`
 *   - `useNavigate`          → `useNavigate(to, { replace })`
 *   - `useSearchParams`      → first element of RR's `[searchParams, set]`
 */
function LinkAdapter({ href, children, ...rest }: NavLinkProps) {
  return (
    <RRLink to={href} {...rest}>
      {children}
    </RRLink>
  );
}

function ImageAdapter({ src, alt, width, height, className }: NavImageProps) {
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt={alt} width={width} height={height} className={className} />;
}

export const rrAdapter: NavigationAdapter = {
  Link: LinkAdapter,
  Image: ImageAdapter,
  usePathname: () => useLocation().pathname,
  useNavigate: () => {
    const nav = useRRNavigate();
    return (path: string, opts?: { replace?: boolean }) => {
      nav(path, { replace: opts?.replace ?? false });
    };
  },
  useSearchParams: () => {
    const [sp] = useRRSearchParams();
    return sp;
  },
};
